import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Snackbar,
  Tooltip,
  Typography,
  ListItemButton,
} from "@mui/material";
import TopNavBar from "../components/TopNavBar";
import { useAuth } from "../context/AuthContext";
import { createJob, getAllJobs, updateJob } from "../api/jobs";
import {
  getAssignmentPeriods,
  getAssignmentPeriodsByJob,
} from "../api/jobAssignmentPeriods";
import {
  getRouteTemplates,
  getRouteTemplateById,
  updateRoute,
} from "../api/routeTemplates";
import { getAllTaskMetadata } from "../api/listTypes";
import { getDevices } from "../api/devices";
import { getCustomers } from "../api/customer";
import { getAllUsers } from "../api/users";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MinimizeOutlinedIcon from "@mui/icons-material/MinimizeOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import JobsMockSummaryStrip from "../components/customerOperationsDashboard/JobsSummaryStrip";
import JobsMockQueue from "../components/customerOperationsDashboard/JobsQueue";
import JobsMockInspector from "../components/customerOperationsDashboard/JobsInspector";
import JobsAssignmentPanel from "../components/customerOperationsDashboard/JobsAssignmentPanel";
import FloatingPanelWindow, {
  FloatingPanelDock,
  FloatingPanelDockTray,
} from "../components/customerOperationsDashboard/FloatingPanelWindow";
import RouteTemplatesDrawer from "../components/customerOperationsDashboard/RouteTemplatesDrawer";

const isCompletedJob = (job) => {
  const status = job?.status?.toLowerCase();
  return (
    status === "completed" || status === "cancelled" || status === "failed"
  );
};

const getDateBucket = (job) => {
  const sourceDate = job?.scheduled_start || job?.created_at;
  if (!sourceDate) return "all";

  const target = new Date(sourceDate);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const targetKey = target.toDateString();
  if (targetKey === today.toDateString()) return "today";
  if (targetKey === tomorrow.toDateString()) return "tomorrow";
  return "all";
};

const toDateKey = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const UNASSIGNED_TECHNICIAN_FILTER_VALUE = "__unassigned__";

// Operations dashboard only needs jobs within a +/-2 week window of today.
const JOB_FETCH_WINDOW_DAYS = 14;
const getJobFetchWindow = () => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - JOB_FETCH_WINDOW_DAYS);
  const end = new Date(today);
  end.setDate(end.getDate() + JOB_FETCH_WINDOW_DAYS);

  return { startDate: toDateKey(start), endDate: toDateKey(end) };
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
// Upper bound on how wide a single jobs fetch window can grow, regardless of
// how large a date range the user picks in the UI.
const JOB_WINDOW_MAX_SPAN_DAYS = 180;

const TIMELINE_BUCKET_DAYS = 7;

const getWeekStart = (date) => {
  const weekStart = new Date(date);
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  return weekStart;
};

const getIsoWeekNumber = (date) => {
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  target.setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7));
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  firstThursday.setDate(
    firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7),
  );
  return (
    1 + Math.round((target.getTime() - firstThursday.getTime()) / 604800000)
  );
};

const formatShortDate = (date) =>
  date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });

// Splits items sequentially into `columnCount` near-equal length chunks (e.g. 49 items -> 17/17/15)
const splitIntoColumns = (items, columnCount = 3) => {
  const perColumn = Math.ceil(items.length / columnCount) || 0;
  const columns = [];
  for (let i = 0; i < columnCount; i += 1) {
    const start = i * perColumn;
    columns.push(items.slice(start, start + perColumn));
  }
  return columns;
};

export default function ServiceDashboardPage() {
  const { user } = useAuth();
  const username = user?.username;
  const role = user?.role?.toLowerCase();
  const isAdmin = role === "admin";
  const isCustomer = false;
  // Admin dashboard is not scoped to a single customer, so job creation
  // always shows the customer/location selectors.
  const customerPkId = "";

  const todayDateKey = useMemo(() => toDateKey(new Date()), []);

  const [jobs, setJobs] = useState([]);
  const [routeTemplates, setRouteTemplates] = useState([]);
  const [assignmentPeriods, setAssignmentPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [rangeStart, setRangeStart] = useState(todayDateKey);
  const [rangeEnd, setRangeEnd] = useState(todayDateKey);
  const [masterRange, setMasterRange] = useState({
    start: todayDateKey,
    end: todayDateKey,
    signal: 0,
  });
  const [timelineWeekOffset, setTimelineWeekOffset] = useState(0);
  const [quickFilter, setQuickFilter] = useState("all");
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedJobPeriods, setSelectedJobPeriods] = useState([]);
  const [jobPeriodsByJobId, setJobPeriodsByJobId] = useState({});
  const [routeTemplatesDrawerOpen, setRouteTemplatesDrawerOpen] =
    useState(false);
  const [routePickerActive, setRoutePickerActive] = useState(false);
  const [pickedRouteForAssignment, setPickedRouteForAssignment] =
    useState(null);
  const [queueSelectedJobIds, setQueueSelectedJobIds] = useState([]);
  const [selectedRouteTemplateId, setSelectedRouteTemplateId] = useState(null);
  const [routeTemplateDetail, setRouteTemplateDetail] = useState(null);
  const [routeTemplateDetailLoading, setRouteTemplateDetailLoading] =
    useState(false);
  const [expandedRouteIds, setExpandedRouteIds] = useState(() => new Set());
  const [pendingRouteAssignments, setPendingRouteAssignments] = useState({});
  const [savingRouteId, setSavingRouteId] = useState(null);
  const [pendingManualAssignments, setPendingManualAssignments] = useState({});
  const [savingManualRouteId, setSavingManualRouteId] = useState(null);
  const [pendingEffectiveDates, setPendingEffectiveDates] = useState({});
  const [taskMetadata, setTaskMetadata] = useState(null);
  const [devices, setDevices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [inspectorWindows, setInspectorWindows] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [periodQueueScope, setPeriodQueueScope] = useState(null);
  const [clearPeriodSelectionSignal, setClearPeriodSelectionSignal] =
    useState(0);
  const [activeDrag, setActiveDrag] = useState({
    active: false,
    windowId: null,
  });
  const inspectorDragRef = useRef({ offsetX: 0, offsetY: 0 });
  const inspectorZCounterRef = useRef(1);
  const periodScopeRangeBackupRef = useRef({ start: null, end: null });
  // Tracks the date range currently covered by the loaded `jobs` state, so we
  // can detect when the timeline is scrolled outside it and fetch more.
  const jobsWindowRef = useRef(getJobFetchWindow());
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [rangeRefreshing, setRangeRefreshing] = useState(false);

  const fetchJobs = useCallback(
    async ({ silent = false, startDate, endDate } = {}) => {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const effectiveWindow =
        startDate && endDate
          ? { startDate, endDate }
          : jobsWindowRef.current || getJobFetchWindow();

      try {
        const data = await getAllJobs(effectiveWindow);
        const jobsArray = Array.isArray(data)
          ? data
          : Array.isArray(data?.jobs)
            ? data.jobs
            : Array.isArray(data?.data)
              ? data.data
              : [];
        setJobs(jobsArray);
        jobsWindowRef.current = effectiveWindow;
      } catch (err) {
        console.error("Error loading jobs mock:", err.message);
        if (!silent) {
          setError("Failed to load jobs for the mock page");
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [],
  );

  // Expands the loaded jobs window (and refetches) if the visible date range
  // falls outside what's currently loaded, so timeline counts stay accurate.
  const ensureJobsWindowCovers = useCallback(
    async (neededStartKey, neededEndKey) => {
      const current = jobsWindowRef.current;
      if (
        current &&
        neededStartKey >= current.startDate &&
        neededEndKey <= current.endDate
      ) {
        return;
      }

      const nextStart =
        current && current.startDate < neededStartKey
          ? current.startDate
          : neededStartKey;
      const nextEnd =
        current && current.endDate > neededEndKey
          ? current.endDate
          : neededEndKey;

      // Cap how much we fetch in one go — an unbounded range (e.g. "all time")
      // would pull far more data than the UI ever needs. Keep the most recent
      // side of the requested range and trim the older end to fit the cap.
      const spanDays = (new Date(nextEnd) - new Date(nextStart)) / MS_PER_DAY;
      let clampedStart = nextStart;
      if (spanDays > JOB_WINDOW_MAX_SPAN_DAYS) {
        const clampedStartDate = new Date(nextEnd);
        clampedStartDate.setDate(
          clampedStartDate.getDate() - JOB_WINDOW_MAX_SPAN_DAYS,
        );
        clampedStart = toDateKey(clampedStartDate);
        console.warn(
          `Requested job date range exceeds ${JOB_WINDOW_MAX_SPAN_DAYS} days; clamped to ${clampedStart}..${nextEnd}.`,
        );
      }

      setTimelineLoading(true);
      try {
        await fetchJobs({
          silent: true,
          startDate: clampedStart,
          endDate: nextEnd,
        });
      } finally {
        setTimelineLoading(false);
      }
    },
    [fetchJobs],
  );

  const refreshCurrentJobsRange = useCallback(async () => {
    if (!rangeStart && !rangeEnd) {
      return;
    }

    setPeriodQueueScope(null);
    setQuickFilter("all");
    setClearPeriodSelectionSignal((current) => current + 1);

    const effectiveStart =
      rangeStart && rangeEnd && rangeStart > rangeEnd ? rangeEnd : rangeStart;
    const effectiveEnd =
      rangeStart && rangeEnd && rangeStart > rangeEnd ? rangeStart : rangeEnd;

    setRangeRefreshing(true);
    try {
      const response = await getAllJobs({
        startDate: effectiveStart || undefined,
        endDate: effectiveEnd || undefined,
      });
      const refreshedJobs = Array.isArray(response)
        ? response
        : Array.isArray(response?.jobs)
          ? response.jobs
          : Array.isArray(response?.data)
            ? response.data
            : [];

      setJobs((currentJobs) => {
        const jobsOutsideRange = currentJobs.filter((job) => {
          const dateKey = toDateKey(job?.scheduled_start || job?.created_at);
          if (!dateKey) return true;
          if (effectiveStart && dateKey < effectiveStart) return true;
          if (effectiveEnd && dateKey > effectiveEnd) return true;
          return false;
        });

        const mergedJobs = new Map(
          jobsOutsideRange.map((job) => [String(job.pk_id), job]),
        );
        refreshedJobs.forEach((job) => {
          mergedJobs.set(String(job.pk_id), job);
        });

        return Array.from(mergedJobs.values());
      });

      setSnackbar({
        open: true,
        message: "Current job range refreshed",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to refresh current job range",
        severity: "error",
      });
    } finally {
      setRangeRefreshing(false);
    }
  }, [rangeEnd, rangeStart]);

  // Debounce so rapid date-range edits in the Jobs Queue filter (or the
  // master range applied from the timeline) don't trigger a fetch per keystroke.
  const rangeFetchTimeoutRef = useRef(null);
  useEffect(() => {
    if (!rangeStart || !rangeEnd) {
      return undefined;
    }

    if (rangeFetchTimeoutRef.current) {
      clearTimeout(rangeFetchTimeoutRef.current);
    }

    rangeFetchTimeoutRef.current = setTimeout(() => {
      ensureJobsWindowCovers(rangeStart, rangeEnd);
    }, 300);

    return () => {
      if (rangeFetchTimeoutRef.current) {
        clearTimeout(rangeFetchTimeoutRef.current);
      }
    };
  }, [ensureJobsWindowCovers, rangeEnd, rangeStart]);

  const fetchAssignmentPeriods = useCallback(async () => {
    try {
      const response = await getAssignmentPeriods({ include_draft: isAdmin });
      setAssignmentPeriods(
        Array.isArray(response?.periods) ? response.periods : [],
      );
    } catch (err) {
      console.error("Error loading assignment periods:", err.message);
    }
  }, [isAdmin]);

  // Admin oversees every customer, so route templates are loaded unscoped.
  const fetchRouteTemplates = useCallback(async () => {
    try {
      const data = await getRouteTemplates();
      const templatesArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.templates)
          ? data.templates
          : [];
      setRouteTemplates(templatesArray);
    } catch (err) {
      console.error("Error loading route templates:", err.message);
    }
  }, []);

  const handleOpenRouteTemplateDetail = useCallback(async (templateId) => {
    setSelectedRouteTemplateId(templateId);
    setRouteTemplateDetailLoading(true);
    try {
      const response = await getRouteTemplateById(templateId);
      setRouteTemplateDetail(response?.template || null);
    } catch (err) {
      console.error("Error loading route template detail:", err.message);
      setRouteTemplateDetail(null);
    } finally {
      setRouteTemplateDetailLoading(false);
    }
  }, []);

  const handleBackToRouteTemplateList = useCallback(() => {
    setSelectedRouteTemplateId(null);
    setRouteTemplateDetail(null);
    setExpandedRouteIds(new Set());
    setPendingRouteAssignments({});
    setPendingManualAssignments({});
    setPendingEffectiveDates({});
  }, []);

  const handleCloseRouteTemplatesDrawer = useCallback(() => {
    setRouteTemplatesDrawerOpen(false);
    setSelectedRouteTemplateId(null);
    setRouteTemplateDetail(null);
    setExpandedRouteIds(new Set());
    setPendingRouteAssignments({});
    setPendingManualAssignments({});
    setPendingEffectiveDates({});
  }, []);

  const handleToggleRouteExpand = useCallback((routeId) => {
    setExpandedRouteIds((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) {
        next.delete(routeId);
      } else {
        next.add(routeId);
      }
      return next;
    });
  }, []);

  const handleRouteAssigneeChange = useCallback((routeId, value) => {
    setPendingRouteAssignments((prev) => ({ ...prev, [routeId]: value }));
  }, []);

  const handleManualAssigneeChange = useCallback((routeId, value) => {
    setPendingManualAssignments((prev) => ({ ...prev, [routeId]: value }));
  }, []);

  const handleEffectiveDateChange = useCallback((routeId, value) => {
    setPendingEffectiveDates((prev) => ({ ...prev, [routeId]: value }));
  }, []);

  const refreshJobsForAssignments = useCallback(async () => {
    await fetchJobs({ silent: true });
    await fetchAssignmentPeriods();

    if (!selectedJobId) {
      setSelectedJobPeriods([]);
      return;
    }

    const response = await getAssignmentPeriodsByJob(selectedJobId, {
      include_draft: isAdmin,
    });
    setSelectedJobPeriods(
      Array.isArray(response?.periods) ? response.periods : [],
    );
  }, [fetchAssignmentPeriods, fetchJobs, isAdmin, selectedJobId]);

  useEffect(() => {
    if (!selectedJobId) {
      setSelectedJobPeriods([]);
      return;
    }

    let active = true;

    const loadSelectedJobPeriods = async () => {
      try {
        const response = await getAssignmentPeriodsByJob(selectedJobId, {
          include_draft: isAdmin,
        });

        if (!active) {
          return;
        }

        setSelectedJobPeriods(
          Array.isArray(response?.periods) ? response.periods : [],
        );
      } catch (err) {
        if (active) {
          console.error("Error loading selected job periods:", err.message);
          setSelectedJobPeriods([]);
        }
      }
    };

    loadSelectedJobPeriods();

    return () => {
      active = false;
    };
  }, [isAdmin, selectedJobId]);

  const handleCreateManualJob = useCallback(
    async (draft) => {
      const filteredTasks = Array.isArray(draft.tasks)
        ? draft.tasks.filter((task) => task.service_id && task.task_id)
        : [];

      // customer_id/location_id/department_id/room_id are intentionally
      // omitted here — the API derives the job hierarchy from the selected
      // device, which is the authoritative source of that data.
      const formData = {
        service_type_id: draft.service_type_id,
        device_id: draft.device_id,
        scheduled_start: draft.scheduled_start || "",
        scheduled_end: draft.scheduled_end || "",
        assigned_technician_id: draft.assigned_technician_id || "",
        status: draft.status || "pending",
        priority: draft.priority || "medium",
        external_notes: draft.external_notes || "",
        internal_notes: isAdmin ? draft.internal_notes || "" : "",
        created_by: draft.created_by || user?.pk_id || user?.id || "",
        total_job_tasks: filteredTasks.length,
      };

      const jobData = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => value !== ""),
      );
      jobData.tasks = filteredTasks;

      const created = await createJob(jobData);
      await fetchJobs({ silent: true });
      if (created?.pk_id) {
        setSelectedJobId(created.pk_id);
      }
      setSnackbar({
        open: true,
        message: "Job created successfully",
        severity: "success",
      });
      return created;
    },
    [fetchJobs, isAdmin, user],
  );

  const handleUpdateManualJob = useCallback(async (jobPkId, updates) => {
    const updatedResponse = await updateJob(jobPkId, updates);
    const updatedJob =
      updatedResponse?.job || updatedResponse?.data || updatedResponse || null;

    setJobs((prevJobs) =>
      prevJobs.map((row) => {
        if (String(row.pk_id) !== String(jobPkId)) {
          return row;
        }

        const tasks = Array.isArray(updatedJob?.tasks)
          ? updatedJob.tasks
          : Array.isArray(updates.tasks)
            ? updates.tasks
            : Array.isArray(row.tasks)
              ? row.tasks
              : [];

        return {
          ...row,
          ...updates,
          ...(updatedJob || {}),
          tasks,
          total_job_tasks: updatedJob?.total_job_tasks ?? tasks.length,
          updated_at: updatedJob?.updated_at || new Date().toISOString(),
        };
      }),
    );

    setSnackbar({
      open: true,
      message: "Job updated successfully",
      severity: "success",
    });

    return updatedJob;
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    fetchAssignmentPeriods();
  }, [fetchAssignmentPeriods]);

  useEffect(() => {
    fetchRouteTemplates();
  }, [fetchRouteTemplates]);

  useEffect(() => {
    let mounted = true;

    const loadCreateJobDependencies = async () => {
      try {
        const [metadata, allDevices, allCustomers, allUsers] =
          await Promise.all([
            getAllTaskMetadata(),
            getDevices(),
            getCustomers(),
            getAllUsers(),
          ]);

        if (!mounted) {
          return;
        }

        setTaskMetadata(metadata);
        setDevices(Array.isArray(allDevices) ? allDevices : []);
        setCustomers(Array.isArray(allCustomers) ? allCustomers : []);
        setUsers(Array.isArray(allUsers) ? allUsers : []);
      } catch (err) {
        console.error("Error loading create job dependencies:", err.message);
      }
    };

    loadCreateJobDependencies();

    return () => {
      mounted = false;
    };
  }, []);

  const scopedUsers = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    const activeValidUsers = list.filter((row) => {
      const activeValue = row?.active;
      if (activeValue === undefined || activeValue === null) {
        return true;
      }
      return (
        activeValue === true ||
        activeValue === 1 ||
        String(activeValue).toLowerCase() === "true"
      );
    });

    if (isAdmin) {
      return activeValidUsers;
    }

    const currentCustomerId =
      user?.customerPkId !== undefined && user?.customerPkId !== null
        ? String(user.customerPkId)
        : "";

    if (currentCustomerId) {
      return activeValidUsers.filter(
        (row) => String(row?.customer_pk_id || "") === currentCustomerId,
      );
    }

    const currentUserId =
      user?.pk_id !== undefined && user?.pk_id !== null
        ? String(user.pk_id)
        : user?.userId !== undefined && user?.userId !== null
          ? String(user.userId)
          : "";

    if (!currentUserId) {
      return [];
    }

    return activeValidUsers.filter(
      (row) => String(row?.pk_id || "") === currentUserId,
    );
  }, [isAdmin, user, users]);

  const assignableTechnicianOptions = useMemo(
    () =>
      scopedUsers
        .map((row) => ({
          value: String(row.pk_id),
          label: row.uk_username || row.uk_email || `User ${row.pk_id}`,
          // Carried through so JobsAssignmentPanel can block assigning a
          // customer-tied account to a period whose route belongs to a
          // different customer. Accounts with no customer id are global.
          customerPkId: row?.customer_pk_id ?? row?.customer_id ?? null,
          role: String(row?.role || "")
            .trim()
            .toLowerCase(),
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [scopedUsers],
  );

  // This admin dashboard has no single customer scope, but each route
  // template belongs to exactly one customer - so route assignee options are
  // scoped per-template instead: never offer "control" accounts, and any
  // account tied to a specific customer (any role, not just "customer") must
  // belong to this template's customer. Accounts with no customer_id at all
  // (internal/global accounts) remain assignable everywhere.
  const routeTemplateCustomerId = useMemo(
    () => String(routeTemplateDetail?.customer_id ?? ""),
    [routeTemplateDetail],
  );

  const routeAssignableTechnicianOptions = useMemo(() => {
    const list = Array.isArray(users) ? users : [];

    return list
      .filter((row) => {
        const activeValue = row?.active;
        const isActive =
          activeValue === undefined ||
          activeValue === null ||
          activeValue === true ||
          activeValue === 1 ||
          String(activeValue).toLowerCase() === "true";
        if (!isActive) return false;

        const roleValue = String(row?.role || "")
          .trim()
          .toLowerCase();
        if (roleValue === "control") return false;

        const rawCustomerId = row?.customer_pk_id ?? row?.customer_id;
        const hasCustomerId =
          rawCustomerId !== undefined &&
          rawCustomerId !== null &&
          String(rawCustomerId) !== "";
        if (hasCustomerId) {
          if (
            !routeTemplateCustomerId ||
            String(rawCustomerId) !== routeTemplateCustomerId
          ) {
            return false;
          }
        }

        return true;
      })
      .map((row) => ({
        value: String(row.pk_id),
        label: row.uk_username || row.uk_email || `User ${row.pk_id}`,
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [users, routeTemplateCustomerId]);

  const handleSaveRouteAssignment = useCallback(
    async (route) => {
      const pendingValue = pendingRouteAssignments[route.pk_id];
      if (pendingValue === undefined) {
        return;
      }

      const nextUserId = pendingValue ? Number(pendingValue) : null;

      setSavingRouteId(route.pk_id);
      try {
        await updateRoute(route.pk_id, {
          defaultAssignedUserId: nextUserId,
          effective_from_date:
            pendingEffectiveDates[route.pk_id] || todayDateKey,
        });
        const nextLabel = nextUserId
          ? routeAssignableTechnicianOptions.find(
              (opt) => String(opt.value) === String(nextUserId),
            )?.label || `User ${nextUserId}`
          : null;

        setRouteTemplateDetail((prev) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            routes: prev.routes.map((r) =>
              r.pk_id === route.pk_id
                ? {
                    ...r,
                    default_assigned_user_id: nextUserId,
                    default_assigned_user_name: nextLabel,
                  }
                : r,
            ),
          };
        });

        setPendingRouteAssignments((prev) => {
          const next = { ...prev };
          delete next[route.pk_id];
          return next;
        });

        setSnackbar({
          open: true,
          message: "Route assignee updated",
          severity: "success",
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.message || "Failed to update route assignee",
          severity: "error",
        });
      } finally {
        setSavingRouteId(null);
      }
    },
    [
      routeAssignableTechnicianOptions,
      pendingRouteAssignments,
      pendingEffectiveDates,
      todayDateKey,
    ],
  );

  const handleSaveManualAssignment = useCallback(
    async (route) => {
      const pendingValue = pendingManualAssignments[route.pk_id];
      if (pendingValue === undefined) {
        return;
      }

      const nextUserId = pendingValue ? Number(pendingValue) : null;

      setSavingManualRouteId(route.pk_id);
      try {
        await updateRoute(route.pk_id, {
          defaultAssignedManualCleaningUserId: nextUserId,
          effective_from_date:
            pendingEffectiveDates[route.pk_id] || todayDateKey,
        });
        const nextLabel = nextUserId
          ? routeAssignableTechnicianOptions.find(
              (opt) => String(opt.value) === String(nextUserId),
            )?.label || `User ${nextUserId}`
          : null;

        setRouteTemplateDetail((prev) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            routes: prev.routes.map((r) =>
              r.pk_id === route.pk_id
                ? {
                    ...r,
                    default_assigned_manual_cleaning_user_id: nextUserId,
                    default_assigned_manual_cleaning_user_name: nextLabel,
                  }
                : r,
            ),
          };
        });

        setPendingManualAssignments((prev) => {
          const next = { ...prev };
          delete next[route.pk_id];
          return next;
        });

        setSnackbar({
          open: true,
          message: "Manual cleaning assignee updated",
          severity: "success",
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.message || "Failed to update manual cleaning assignee",
          severity: "error",
        });
      } finally {
        setSavingManualRouteId(null);
      }
    },
    [
      routeAssignableTechnicianOptions,
      pendingManualAssignments,
      pendingEffectiveDates,
      todayDateKey,
    ],
  );

  const technicianOptions = useMemo(
    () => [
      { value: UNASSIGNED_TECHNICIAN_FILTER_VALUE, label: "Unassigned" },
      ...assignableTechnicianOptions,
    ],
    [assignableTechnicianOptions],
  );

  const filteredByWindowAndSearch = useMemo(() => {
    const term = search.trim().toLowerCase();
    const normalizedStart = rangeStart || "";
    const normalizedEnd = rangeEnd || "";
    const hasRange = Boolean(normalizedStart || normalizedEnd);
    const effectiveStart =
      normalizedStart && normalizedEnd
        ? normalizedStart <= normalizedEnd
          ? normalizedStart
          : normalizedEnd
        : normalizedStart || normalizedEnd;
    const effectiveEnd =
      normalizedStart && normalizedEnd
        ? normalizedStart <= normalizedEnd
          ? normalizedEnd
          : normalizedStart
        : normalizedStart || normalizedEnd;

    return jobs.filter((job) => {
      const jobDateKey = toDateKey(job?.scheduled_start || job?.created_at);
      const timeMatches =
        !hasRange ||
        (jobDateKey &&
          jobDateKey >= effectiveStart &&
          jobDateKey <= effectiveEnd);
      const searchMatches =
        !term ||
        [
          job.job_number,
          job.customer_name,
          job.customer_id,
          job.device_name,
          job.device_id,
          job.assigned_technician_name,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));

      const periodScopeMatches =
        !periodQueueScope ||
        (Array.isArray(periodQueueScope.jobIds) &&
          periodQueueScope.jobIds.length > 0 &&
          periodQueueScope.jobIds.includes(String(job.pk_id)));

      return timeMatches && searchMatches && periodScopeMatches;
    });
  }, [jobs, periodQueueScope, rangeEnd, rangeStart, search]);

  const handlePeriodQueueScopeChange = useCallback(
    (scope) => {
      if (!scope) {
        setPeriodQueueScope(null);
        setRangeStart(periodScopeRangeBackupRef.current.start || "");
        setRangeEnd(periodScopeRangeBackupRef.current.end || "");
        return;
      }

      const nextStart = toDateKey(scope.periodStart);
      const nextEnd = toDateKey(scope.periodEnd || scope.periodStart);

      if (!periodQueueScope) {
        periodScopeRangeBackupRef.current = {
          start: rangeStart,
          end: rangeEnd,
        };
      }

      setPeriodQueueScope({
        periodId: String(scope.periodId),
        routeName: scope.routeName || "",
        label: scope.label || "default",
        status: scope.status || "draft",
        jobIds: Array.isArray(scope.jobIds)
          ? scope.jobIds.map((id) => String(id))
          : [],
      });

      setRangeStart(nextStart || "");
      setRangeEnd(nextEnd || "");
    },
    [periodQueueScope, rangeEnd, rangeStart],
  );

  const handleClearPeriodQueueScope = useCallback(() => {
    setPeriodQueueScope(null);
    setRangeStart(periodScopeRangeBackupRef.current.start || "");
    setRangeEnd(periodScopeRangeBackupRef.current.end || "");
    setClearPeriodSelectionSignal((prev) => prev + 1);
  }, []);

  const quickFilterCounts = useMemo(() => {
    const countBy = {
      unassigned: 0,
      assigned: 0,
      in_progress: 0,
      completed: 0,
      on_hold: 0,
      failed: 0,
    };

    filteredByWindowAndSearch.forEach((job) => {
      const status = String(job.status || "").toLowerCase();
      const hasTechnician = Boolean(
        job.assigned_technician_id || job.assigned_technician_name,
      );

      if (!hasTechnician) countBy.unassigned += 1;
      if (hasTechnician) countBy.assigned += 1;
      if (status === "in_progress" || status === "in progress")
        countBy.in_progress += 1;
      if (status === "completed") countBy.completed += 1;
      if (status === "on_hold" || status === "on hold" || status === "hold") {
        countBy.on_hold += 1;
      }
      if (status === "failed") countBy.failed += 1;
    });

    return countBy;
  }, [filteredByWindowAndSearch]);

  const filteredJobs = useMemo(() => {
    if (quickFilter === "all") return filteredByWindowAndSearch;

    return filteredByWindowAndSearch.filter((job) => {
      const status = String(job.status || "").toLowerCase();
      const hasTechnician = Boolean(
        job.assigned_technician_id || job.assigned_technician_name,
      );

      switch (quickFilter) {
        case "unassigned":
          return !hasTechnician;
        case "assigned":
          return hasTechnician;
        case "in_progress":
          return status === "in_progress" || status === "in progress";
        case "completed":
          return status === "completed";
        case "on_hold":
          return (
            status === "on_hold" || status === "on hold" || status === "hold"
          );
        case "failed":
          return status === "failed";
        default:
          return true;
      }
    });
  }, [filteredByWindowAndSearch, quickFilter]);

  useEffect(() => {
    if (!Array.isArray(filteredJobs) || filteredJobs.length === 0) {
      setJobPeriodsByJobId({});
      return;
    }

    let active = true;

    const loadVisibleJobPeriods = async () => {
      try {
        const responseList = await Promise.all(
          filteredJobs
            .filter((job) => job?.pk_id)
            .map(async (job) => {
              const response = await getAssignmentPeriodsByJob(job.pk_id, {
                include_draft: isAdmin,
              });

              return {
                jobId: String(job.pk_id),
                periods: Array.isArray(response?.periods)
                  ? response.periods
                  : [],
              };
            }),
        );

        if (!active) {
          return;
        }

        const nextMap = responseList.reduce((acc, entry) => {
          acc[entry.jobId] = entry.periods;
          return acc;
        }, {});

        setJobPeriodsByJobId(nextMap);
      } catch (err) {
        if (active) {
          console.error("Error loading job route memberships:", err.message);
          setJobPeriodsByJobId({});
        }
      }
    };

    loadVisibleJobPeriods();

    return () => {
      active = false;
    };
  }, [filteredJobs, isAdmin]);

  useEffect(() => {
    if (filteredJobs.length === 0) {
      setSelectedJobId(null);
      return;
    }

    const exists = filteredJobs.some(
      (job) => String(job.pk_id) === String(selectedJobId),
    );

    if (!exists) {
      setSelectedJobId(filteredJobs[0].pk_id);
    }
  }, [filteredJobs, selectedJobId]);

  const jobsById = useMemo(() => {
    const map = new Map();
    jobs.forEach((job) => {
      if (!job?.pk_id) return;
      map.set(String(job.pk_id), job);
    });
    return map;
  }, [jobs]);

  const handleOpenInspectorWindow = useCallback((jobPkId) => {
    if (!jobPkId) return;

    const normalizedJobId = String(jobPkId);

    setSelectedJobId(jobPkId);
    setInspectorWindows((prev) => {
      const nextZ = inspectorZCounterRef.current + 1;
      inspectorZCounterRef.current = nextZ;

      const existingIndex = prev.findIndex(
        (windowItem) =>
          windowItem.mode === "view" &&
          String(windowItem.jobId) === normalizedJobId,
      );

      if (existingIndex >= 0) {
        return prev.map((windowItem, idx) =>
          idx === existingIndex
            ? { ...windowItem, minimized: false, zIndex: nextZ }
            : windowItem,
        );
      }

      const indexOffset = prev.length % 6;
      return [
        ...prev,
        {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          jobId: normalizedJobId,
          mode: "view",
          unlocked: false,
          dirty: false,
          minimized: false,
          x: 70 + indexOffset * 24,
          y: 140 + indexOffset * 18,
          zIndex: nextZ,
        },
      ];
    });
  }, []);

  const handleOpenCreateWindow = useCallback(() => {
    const nextZ = inspectorZCounterRef.current + 1;
    inspectorZCounterRef.current = nextZ;
    const indexOffset = inspectorWindows.length % 6;
    const defaultScheduledStart = rangeStart ? `${rangeStart}T00:00` : null;

    setInspectorWindows((prev) => [
      ...prev,
      {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        jobId: null,
        mode: "create",
        unlocked: true,
        dirty: false,
        minimized: false,
        x: 70 + indexOffset * 24,
        y: 140 + indexOffset * 18,
        zIndex: nextZ,
        defaultScheduledStart,
      },
    ]);
  }, [inspectorWindows.length, rangeStart]);

  const bringInspectorToFront = useCallback((windowId) => {
    const nextZ = inspectorZCounterRef.current + 1;
    inspectorZCounterRef.current = nextZ;

    setInspectorWindows((prev) =>
      prev.map((windowItem) =>
        windowItem.id === windowId
          ? { ...windowItem, zIndex: nextZ }
          : windowItem,
      ),
    );
  }, []);

  const handleCloseInspectorWindow = useCallback((windowId) => {
    setInspectorWindows((prev) =>
      prev.filter((windowItem) => windowItem.id !== windowId),
    );
  }, []);

  const handleToggleInspectorMinimize = useCallback(
    (windowId, minimized) => {
      if (!minimized) {
        bringInspectorToFront(windowId);
      }

      setInspectorWindows((prev) =>
        prev.map((windowItem) =>
          windowItem.id === windowId
            ? { ...windowItem, minimized }
            : windowItem,
        ),
      );
    },
    [bringInspectorToFront],
  );

  const handleToggleInspectorLock = useCallback((windowId) => {
    const nextZ = inspectorZCounterRef.current + 1;
    inspectorZCounterRef.current = nextZ;

    setInspectorWindows((prev) =>
      prev.map((windowItem) =>
        windowItem.id === windowId
          ? {
              ...windowItem,
              unlocked: !windowItem.unlocked,
              zIndex: nextZ,
            }
          : windowItem,
      ),
    );
  }, []);

  const handleInspectorDirtyChange = useCallback((windowId, dirty) => {
    setInspectorWindows((prev) => {
      let changed = false;
      const next = prev.map((windowItem) => {
        if (windowItem.id !== windowId) {
          return windowItem;
        }

        const normalizedDirty = Boolean(dirty);
        if (windowItem.dirty === normalizedDirty) {
          return windowItem;
        }

        changed = true;
        return { ...windowItem, dirty: normalizedDirty };
      });

      return changed ? next : prev;
    });
  }, []);

  const handleInspectorDragStart = useCallback(
    (windowId, event) => {
      if (event.button !== 0) {
        return;
      }

      const targetWindow = inspectorWindows.find(
        (windowItem) => windowItem.id === windowId,
      );
      if (!targetWindow || targetWindow.minimized) {
        return;
      }

      bringInspectorToFront(windowId);

      inspectorDragRef.current = {
        offsetX: event.clientX - targetWindow.x,
        offsetY: event.clientY - targetWindow.y,
      };
      setActiveDrag({ active: true, windowId });
      event.preventDefault();
    },
    [bringInspectorToFront, inspectorWindows],
  );

  useEffect(() => {
    if (!activeDrag.active || !activeDrag.windowId) {
      return;
    }

    const handleMouseMove = (event) => {
      const minLeft = 12;
      const minTop = 72;
      const maxLeft = Math.max(12, window.innerWidth - 320);
      const maxTop = Math.max(72, window.innerHeight - 80);

      const nextLeft = Math.min(
        maxLeft,
        Math.max(minLeft, event.clientX - inspectorDragRef.current.offsetX),
      );
      const nextTop = Math.min(
        maxTop,
        Math.max(minTop, event.clientY - inspectorDragRef.current.offsetY),
      );

      setInspectorWindows((prev) =>
        prev.map((windowItem) =>
          windowItem.id === activeDrag.windowId
            ? { ...windowItem, x: nextLeft, y: nextTop }
            : windowItem,
        ),
      );
    };

    const handleMouseUp = () => {
      setActiveDrag({ active: false, windowId: null });
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [activeDrag]);

  const minimizedInspectorWindows = useMemo(
    () => inspectorWindows.filter((windowItem) => windowItem.minimized),
    [inspectorWindows],
  );

  const metrics = useMemo(() => {
    const uniqueTechs = new Set(
      jobs
        .map(
          (job) => job.assigned_technician_id || job.assigned_technician_name,
        )
        .filter(Boolean),
    );
    const todaysJobs = jobs.filter((job) => getDateBucket(job) === "today");

    return [
      { label: "Visible", value: todaysJobs.length, highlight: true },
      {
        label: "Open",
        value: todaysJobs.filter((job) => !isCompletedJob(job)).length,
      },
      {
        label: "Completed",
        value: todaysJobs.filter((job) => isCompletedJob(job)).length,
      },
      {
        label: "Today",
        value: todaysJobs.length,
      },
      {
        label: "Tomorrow",
        value: jobs.filter((job) => getDateBucket(job) === "tomorrow").length,
      },
      { label: "Techs Active", value: uniqueTechs.size },
    ];
  }, [jobs]);

  const activeTechnicians = useMemo(() => {
    const todaysAssignedJobs = jobs.filter(
      (job) =>
        getDateBucket(job) === "today" &&
        Boolean(job.assigned_technician_id || job.assigned_technician_name),
    );
    const jobsByTechnician = new Map();

    todaysAssignedJobs.forEach((job) => {
      const technicianId =
        job.assigned_technician_id || job.assigned_technician_name;
      const technicianKey = String(technicianId);
      const technicianJobs = jobsByTechnician.get(technicianKey) || [];
      technicianJobs.push(job);
      jobsByTechnician.set(technicianKey, technicianJobs);
    });

    return Array.from(jobsByTechnician.entries()).map(
      ([technicianId, technicianJobs]) => {
        const orderedJobs = technicianJobs.slice().sort((left, right) => {
          const statusRank = (job) => {
            const status = String(job.status || "").toLowerCase();
            if (status === "in_progress") return 0;
            if (!isCompletedJob(job)) return 1;
            return 2;
          };
          const rankDifference = statusRank(left) - statusRank(right);
          if (rankDifference !== 0) return rankDifference;

          const leftTime = new Date(left.scheduled_start || 0).getTime();
          const rightTime = new Date(right.scheduled_start || 0).getTime();
          return leftTime - rightTime;
        });
        const representativeJob = orderedJobs[0];
        const totalJobs = technicianJobs.length;
        const completedJobs = technicianJobs.filter(isCompletedJob).length;
        const progressPercent =
          totalJobs > 0 ? Math.round((completedJobs / totalJobs) * 100) : 0;

        return {
          id: technicianId,
          name: representativeJob.assigned_technician_name || technicianId,
          completedJobs,
          totalJobs,
          progressPercent,
          roomName: representativeJob.room_name || "-",
        };
      },
    );
  }, [jobs]);

  const routeCountsByDateKey = useMemo(() => {
    const counts = new Map();
    assignmentPeriods.forEach((period) => {
      const dateKey = toDateKey(period.period_start || period.created_at);
      if (!dateKey) return;
      counts.set(dateKey, (counts.get(dateKey) || 0) + 1);
    });
    return counts;
  }, [assignmentPeriods]);

  // When the visible timeline week scrolls outside the loaded jobs window,
  // fetch the extra range so the counts for that week aren't stuck at 0.
  useEffect(() => {
    const weekStart = getWeekStart(new Date());
    weekStart.setDate(weekStart.getDate() + timelineWeekOffset);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + TIMELINE_BUCKET_DAYS - 1);

    ensureJobsWindowCovers(toDateKey(weekStart), toDateKey(weekEnd));
  }, [ensureJobsWindowCovers, timelineWeekOffset]);

  const timelineBuckets = useMemo(() => {
    const today = new Date();
    const weekStart = getWeekStart(today);
    weekStart.setDate(weekStart.getDate() + timelineWeekOffset);

    return Array.from({ length: TIMELINE_BUCKET_DAYS }).map((_, index) => {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + index);
      const dateKey = toDateKey(day);
      const isToday = day.toDateString() === today.toDateString();

      const dayJobs = jobs.filter((job) => {
        const sourceDate = job?.scheduled_start || job?.created_at;
        if (!sourceDate) return false;
        return new Date(sourceDate).toDateString() === day.toDateString();
      });

      const count = dayJobs.length;
      const completedCount = dayJobs.filter(
        (job) => String(job.status || "").toLowerCase() === "completed",
      ).length;
      const highUrgentCount = dayJobs.filter((job) => {
        const priority = String(job.priority || "").toLowerCase();
        return priority === "high" || priority === "urgent";
      }).length;
      const routeCount = routeCountsByDateKey.get(dateKey) || 0;

      return {
        label: isToday
          ? "Today"
          : day.toLocaleDateString([], {
              weekday: "short",
              day: "numeric",
            }),
        dateKey,
        count,
        completedCount,
        highUrgentCount,
        routeCount,
        caption: count === 0 ? "clear" : `${count} queued`,
        active: isToday || count > 0,
        selected: rangeStart === dateKey && rangeEnd === dateKey,
      };
    });
  }, [jobs, rangeEnd, rangeStart, routeCountsByDateKey, timelineWeekOffset]);

  const timelineWeekSummary = useMemo(() => {
    const today = new Date();
    const weekStart = getWeekStart(today);
    weekStart.setDate(weekStart.getDate() + timelineWeekOffset);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + TIMELINE_BUCKET_DAYS - 1);

    return {
      label: `Week ${getIsoWeekNumber(weekStart)}`,
      rangeLabel: `${formatShortDate(weekStart)} - ${formatShortDate(weekEnd)}`,
    };
  }, [timelineWeekOffset]);

  const applyMasterRange = useCallback((startDateKey, endDateKey) => {
    setRangeStart(startDateKey);
    setRangeEnd(endDateKey);
    setMasterRange((current) => ({
      start: startDateKey,
      end: endDateKey,
      signal: current.signal + 1,
    }));
    setQuickFilter("all");
    setPeriodQueueScope(null);
  }, []);

  const handleTimelineDaySelect = useCallback(
    (dateKey) => {
      if (!dateKey) {
        return;
      }

      applyMasterRange(dateKey, dateKey);
    },
    [applyMasterRange],
  );

  const handlePreviousTimelineWeek = useCallback(() => {
    setTimelineWeekOffset((current) => current - TIMELINE_BUCKET_DAYS);
  }, []);

  const handleNextTimelineWeek = useCallback(() => {
    setTimelineWeekOffset((current) => current + TIMELINE_BUCKET_DAYS);
  }, []);

  const handleTodayTimelineWeek = useCallback(() => {
    setTimelineWeekOffset(0);
    applyMasterRange(todayDateKey, todayDateKey);
  }, [applyMasterRange, todayDateKey]);

  const handleCalendarTimelineDaySelect = useCallback(
    (dateKey) => {
      if (!dateKey) {
        return;
      }

      const selectedDate = new Date(`${dateKey}T00:00:00`);
      if (Number.isNaN(selectedDate.getTime())) {
        return;
      }

      const currentWeekStart = getWeekStart(new Date());
      const selectedWeekStart = getWeekStart(selectedDate);
      const weekOffset = Math.round(
        (selectedWeekStart.getTime() - currentWeekStart.getTime()) / 86400000,
      );

      setTimelineWeekOffset(weekOffset);
      applyMasterRange(dateKey, dateKey);
    },
    [applyMasterRange],
  );

  return (
    <>
      <TopNavBar
        title="Service Coordinator App - RC Service Tool"
        username={username}
        heading="Service Coordinator App"
      />
      <Container
        maxWidth={false}
        sx={{
          mt: 2,
          pb: 6,
          px: {
            xs: 2,
            sm: 2.5,
            md: 3,
            lg: isAdmin ? 1.5 : 3,
            xl: isAdmin ? 2 : 4,
          },
        }}
      >
        <Box sx={{ display: "grid", gap: 3 }}>
          <JobsMockSummaryStrip
            metrics={metrics}
            timelineBuckets={timelineBuckets}
            onSelectDay={handleTimelineDaySelect}
            onPreviousWeek={handlePreviousTimelineWeek}
            onNextWeek={handleNextTimelineWeek}
            onToday={handleTodayTimelineWeek}
            onSelectCalendarDay={handleCalendarTimelineDaySelect}
            selectedDateKey={
              rangeStart === rangeEnd ? rangeStart : todayDateKey
            }
            weekLabel={timelineWeekSummary.label}
            weekRangeLabel={timelineWeekSummary.rangeLabel}
            timelineWeekOffset={timelineWeekOffset}
            timelineLoading={timelineLoading}
            activeTechnicians={activeTechnicians}
            onOpenSettings={() => setRouteTemplatesDrawerOpen(true)}
          />

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && (
            <>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, lg: 4 }}>
                  <JobsAssignmentPanel
                    visibleJobs={filteredJobs}
                    masterRangeStart={masterRange.start}
                    masterRangeEnd={masterRange.end}
                    masterRangeSignal={masterRange.signal}
                    selectedJobId={selectedJobId}
                    technicianOptions={technicianOptions}
                    user={user}
                    isAdmin={isAdmin}
                    hasMinimizedJobWindows={
                      minimizedInspectorWindows.length > 0
                    }
                    clearSelectionSignal={clearPeriodSelectionSignal}
                    onPeriodSelectionChange={handlePeriodQueueScopeChange}
                    onPeriodDeleted={(deletedPeriodId) => {
                      if (
                        periodQueueScope &&
                        String(periodQueueScope.periodId) ===
                          String(deletedPeriodId)
                      ) {
                        handleClearPeriodQueueScope();
                      }
                    }}
                    onAssignmentsChanged={refreshJobsForAssignments}
                    customer={isCustomer}
                    pickerActive={routePickerActive}
                    onPeriodPicked={(period) => {
                      setPickedRouteForAssignment({
                        periodId: String(period.pk_id),
                        label: period.label || "default",
                      });
                      setRoutePickerActive(false);
                    }}
                    onCancelPeriodPicker={() => setRoutePickerActive(false)}
                    queueSelectedJobIds={queueSelectedJobIds}
                  />
                </Grid>

                <Grid size={{ xs: 12, lg: 8 }}>
                  <Box sx={{ display: "grid", gap: 3 }}>
                    <JobsMockQueue
                      jobs={filteredJobs}
                      selectedJobId={selectedJobId}
                      selectedJobPeriods={selectedJobPeriods}
                      jobPeriodsByJobId={jobPeriodsByJobId}
                      onSelectJob={setSelectedJobId}
                      onJobDoubleTap={handleOpenInspectorWindow}
                      onCreateJob={handleOpenCreateWindow}
                      search={search}
                      onSearchChange={setSearch}
                      rangeStart={rangeStart}
                      rangeEnd={rangeEnd}
                      onRangeStartChange={setRangeStart}
                      onRangeEndChange={setRangeEnd}
                      onClearRange={() => {
                        setRangeStart("");
                        setRangeEnd("");
                      }}
                      onRefreshCurrentRange={refreshCurrentJobsRange}
                      rangeRefreshing={rangeRefreshing}
                      quickFilter={quickFilter}
                      onQuickFilterChange={setQuickFilter}
                      quickFilterCounts={quickFilterCounts}
                      onClearQuickFilter={() => setQuickFilter("all")}
                      hasPeriodScope={Boolean(periodQueueScope)}
                      periodScopeLabel={
                        periodQueueScope
                          ? `Route #${periodQueueScope.periodId}${periodQueueScope.routeName ? ` ${periodQueueScope.routeName}` : ""}`
                          : ""
                      }
                      onClearPeriodScope={handleClearPeriodQueueScope}
                      hideCustomerContext={isCustomer}
                      pickedRoute={pickedRouteForAssignment}
                      onStartRoutePicker={() => setRoutePickerActive(true)}
                      onClearPickedRoute={() =>
                        setPickedRouteForAssignment(null)
                      }
                      onBulkJobsUpdated={refreshJobsForAssignments}
                      onFeedback={(message, severity = "success") =>
                        setSnackbar({ open: true, message, severity })
                      }
                      onSelectionChange={setQueueSelectedJobIds}
                    />
                  </Box>
                </Grid>
              </Grid>
            </>
          )}

          {inspectorWindows.map((windowItem) => {
            const windowJob = jobsById.get(String(windowItem.jobId)) || null;

            const windowTitle =
              windowItem.mode === "create"
                ? "Create Job"
                : windowJob
                  ? `${windowJob.room_name ? `${windowJob.room_name}  /  ` : ""}${
                      windowJob.job_number
                    }${
                      windowJob.route_name
                        ? `  /  ${
                            windowJob.assignment_period_id
                              ? `#${windowJob.assignment_period_id}  /  `
                              : ""
                          }${windowJob.route_name}`
                        : "  /  No Route"
                    }`
                  : `Job ${windowItem.jobId}`;

            return (
              <FloatingPanelWindow
                key={windowItem.id}
                visible={!windowItem.minimized}
                onMouseDown={() => bringInspectorToFront(windowItem.id)}
                position={{ x: windowItem.x, y: windowItem.y }}
                width={620}
                zIndex={1500 + windowItem.zIndex}
                title={windowTitle}
                onHeaderMouseDown={(event) =>
                  handleInspectorDragStart(windowItem.id, event)
                }
                headerCursor={
                  activeDrag.active && activeDrag.windowId === windowItem.id
                    ? "grabbing"
                    : "grab"
                }
                actions={
                  <>
                    {windowItem.mode === "view" ? (
                      <IconButton
                        size="small"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={() => handleToggleInspectorLock(windowItem.id)}
                      >
                        {windowItem.unlocked ? (
                          <LockOpenOutlinedIcon fontSize="small" />
                        ) : (
                          <LockOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    ) : null}
                    <IconButton
                      size="small"
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={() =>
                        handleToggleInspectorMinimize(windowItem.id, true)
                      }
                    >
                      <MinimizeOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={() => handleCloseInspectorWindow(windowItem.id)}
                    >
                      <CloseOutlinedIcon fontSize="small" />
                    </IconButton>
                  </>
                }
              >
                <JobsMockInspector
                  job={windowJob}
                  mode={windowItem.mode}
                  editable={windowItem.mode === "create" || windowItem.unlocked}
                  onCreateJob={handleCreateManualJob}
                  onUpdateJob={handleUpdateManualJob}
                  onDirtyChange={(dirty) =>
                    handleInspectorDirtyChange(windowItem.id, dirty)
                  }
                  onSaveSuccess={(message, severity = "success") =>
                    setSnackbar({
                      open: true,
                      message,
                      severity,
                    })
                  }
                  onRequestClose={() =>
                    handleCloseInspectorWindow(windowItem.id)
                  }
                  taskMetadata={taskMetadata}
                  technicianOptions={assignableTechnicianOptions}
                  devices={devices}
                  customers={customers}
                  user={user}
                  customerPkId={customerPkId}
                  defaultScheduledStart={windowItem.defaultScheduledStart}
                />
              </FloatingPanelWindow>
            );
          })}

          {minimizedInspectorWindows.length > 0 ? (
            <FloatingPanelDockTray zIndex={1600}>
              {minimizedInspectorWindows.map((windowItem) => {
                const windowJob =
                  jobsById.get(String(windowItem.jobId)) || null;
                const windowTitle =
                  windowItem.mode === "create"
                    ? "Create Job"
                    : windowJob?.job_number || `Job ${windowItem.jobId}`;

                return (
                  <FloatingPanelDock
                    key={`dock_${windowItem.id}`}
                    label={windowTitle}
                    indicatorColor={
                      windowItem.mode === "create"
                        ? "primary.main"
                        : windowItem.dirty
                          ? "warning.main"
                          : undefined
                    }
                    onRestore={() =>
                      handleToggleInspectorMinimize(windowItem.id, false)
                    }
                    onClose={() => handleCloseInspectorWindow(windowItem.id)}
                  />
                );
              })}
            </FloatingPanelDockTray>
          ) : null}
        </Box>
      </Container>

      <RouteTemplatesDrawer
        open={routeTemplatesDrawerOpen}
        onClose={handleCloseRouteTemplatesDrawer}
        selectedRouteTemplateId={selectedRouteTemplateId}
        onBackToTemplateList={handleBackToRouteTemplateList}
        routeTemplateDetail={routeTemplateDetail}
        routeTemplateDetailLoading={routeTemplateDetailLoading}
        expandedRouteIds={expandedRouteIds}
        pendingRouteAssignments={pendingRouteAssignments}
        pendingManualAssignments={pendingManualAssignments}
        pendingEffectiveDates={pendingEffectiveDates}
        todayDateKey={todayDateKey}
        savingRouteId={savingRouteId}
        savingManualRouteId={savingManualRouteId}
        assignableTechnicianOptions={assignableTechnicianOptions}
        isAdmin={isAdmin}
        onToggleRouteExpand={handleToggleRouteExpand}
        onRouteAssigneeChange={handleRouteAssigneeChange}
        onManualAssigneeChange={handleManualAssigneeChange}
        onEffectiveDateChange={handleEffectiveDateChange}
        onSaveRouteAssignment={handleSaveRouteAssignment}
        onSaveManualAssignment={handleSaveManualAssignment}
        routeTemplates={routeTemplates}
        onOpenTemplateDetail={handleOpenRouteTemplateDetail}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
        message={snackbar.message}
      />
    </>
  );
}
